/**
 * A sheet that slides over the current screen.
 *
 * Used for things that are needed *during* a session but must never take the arrow's place:
 * inviting somebody else, mostly. It closes on the backdrop, on Escape, and on its own button,
 * because a sheet you cannot dismiss on a phone with one hand is a trap.
 */

import { el } from '../lib/dom';

export interface SheetOptions {
  title: string;
  body?: string;
  content: HTMLElement;
  onClose: () => void;
}

export interface Sheet {
  element: HTMLElement;
  destroy: () => void;
}

export function createSheet(options: SheetOptions): Sheet {
  const close = el('button', { class: 'sheet__close', type: 'button', 'aria-label': 'Close' }, '×');
  close.addEventListener('click', () => options.onClose());

  const panel = el(
    'div',
    { class: 'sheet__panel', role: 'dialog', 'aria-modal': 'true', 'aria-label': options.title },
    el(
      'header',
      { class: 'sheet__head' },
      el('h2', { class: 'sheet__title', text: options.title }),
      close,
    ),
    options.body ? el('p', { class: 'sheet__body', text: options.body }) : null,
    options.content,
  );

  const element = el('div', { class: 'sheet' }, panel);

  // A tap on the backdrop, but not one that started inside the panel and drifted out.
  element.addEventListener('pointerdown', (event) => {
    if (event.target === element) options.onClose();
  });

  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') options.onClose();
  };
  window.addEventListener('keydown', onKey);

  // Focus moves into the sheet so a keyboard or screen reader lands where the eye already is.
  window.requestAnimationFrame(() => close.focus());

  return {
    element,
    destroy: () => {
      window.removeEventListener('keydown', onKey);
      element.remove();
    },
  };
}
